import { Injectable } from '@angular/core';
import { DataSlice } from 'src/app/state/library/library.model';
import { COMPARISON_GRIDS_KEY } from 'src/app/state/library/library.util';

export interface PendingSliceFilterRestore {
  dashboardId: string;
  dashboardVariant?: string;
  filterValues: Record<string, unknown>;
}

/**
 * Multi-select option values are strings; peer defaults / JSON can leave
 * numeric ids. Coerce those arrays so restore matches `option.value`.
 * Do not touch year/period ranges (numeric) or other non-id arrays.
 */
const MULTISELECT_FILTER_KEYS = new Set([
  'states',
  'sub_markets',
  'districts',
  'wz_classes',
  'industries',
  'states_multiselect_a',
  'states_multiselect_b',
  'districts_multiselect_a',
  'districts_multiselect_b',
]);

export function normalizeSliceFilterValues(
  filterValues: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filterValues)) {
    if (Array.isArray(value) && MULTISELECT_FILTER_KEYS.has(key)) {
      out[key] = value.map((item) =>
        item != null && (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean')
          ? `${item}`
          : item,
      );
    } else {
      out[key] = value;
    }
  }
  return out;
}

/** Stages saved slice filter values for one-shot restore after library → analysis navigation. */
@Injectable({ providedIn: 'root' })
export class SliceFilterRestoreService {
  private pending: PendingSliceFilterRestore | null = null;
  /**
   * Identity kept after `consume` so first-load smart defaults (e.g. forecast
   * peer states) can still detect a library restore and skip themselves.
   */
  private restoreIdentity: {
    dashboardId: string;
    dashboardVariant?: string;
  } | null = null;
  private skipSmartDefaults = false;
  /** Normalized values retained after consume for smart-default skip re-apply. */
  private lastAppliedFilterValues: Record<string, unknown> | null = null;
  private pendingComparisonGrids: {
    dashboardId: string;
    dashboardVariant?: string;
    grids: string[];
  } | null = null;

  stage(slice: DataSlice): void {
    const filterValues = normalizeSliceFilterValues(slice.filterValues ?? {});
    this.pending = {
      dashboardId: slice.dashboardId,
      dashboardVariant: slice.dashboardVariant,
      filterValues,
    };
    this.restoreIdentity = {
      dashboardId: slice.dashboardId,
      dashboardVariant: slice.dashboardVariant,
    };
    this.skipSmartDefaults = true;
    this.lastAppliedFilterValues = null;

    const grids = filterValues[COMPARISON_GRIDS_KEY];
    this.pendingComparisonGrids =
      Array.isArray(grids) && grids.length >= 4
        ? {
            dashboardId: slice.dashboardId,
            dashboardVariant: slice.dashboardVariant,
            grids: grids.map((id) => `${id}`),
          }
        : null;
  }

  consume(dashboardId: string, dashboardVariant?: string): Record<string, unknown> | null {
    if (!this._matchesIdentity(this.pending, dashboardId, dashboardVariant)) {
      return null;
    }
    const filterValues = this.pending!.filterValues;
    this.lastAppliedFilterValues = filterValues;
    this.pending = null;
    return filterValues;
  }

  /**
   * When a library → analysis restore was staged for this dashboard, returns the
   * saved filter values and clears the one-shot smart-default skip flag.
   * Dashboards with first-load smart defaults (e.g. forecast peer states) should
   * apply these values (or trust prior consume) and skip those defaults.
   *
   * Null when no library restore is in play for this dashboard.
   */
  takeSliceRestoreSkipSmartDefaults(
    dashboardId: string,
    dashboardVariant?: string,
  ): Record<string, unknown> | null {
    if (!this.skipSmartDefaults) {
      return null;
    }
    if (!this._matchesIdentity(this.restoreIdentity, dashboardId, dashboardVariant)) {
      return null;
    }
    this.skipSmartDefaults = false;
    this.restoreIdentity = null;

    if (this._matchesIdentity(this.pending, dashboardId, dashboardVariant)) {
      const filterValues = this.pending!.filterValues;
      this.lastAppliedFilterValues = filterValues;
      this.pending = null;
      return filterValues;
    }

    const filterValues = this.lastAppliedFilterValues;
    this.lastAppliedFilterValues = null;
    return filterValues;
  }

  /**
   * Four-grid comparison quadrant ids (`comparison_grids`), staged separately
   * because the primary data-controller consume runs before grid controllers exist.
   */
  consumeComparisonGrids(dashboardId: string, dashboardVariant?: string): string[] | null {
    if (!this._matchesIdentity(this.pendingComparisonGrids, dashboardId, dashboardVariant)) {
      return null;
    }
    const grids = this.pendingComparisonGrids!.grids;
    this.pendingComparisonGrids = null;
    return grids;
  }

  clear(): void {
    this.pending = null;
    this.restoreIdentity = null;
    this.skipSmartDefaults = false;
    this.lastAppliedFilterValues = null;
    this.pendingComparisonGrids = null;
  }

  private _matchesIdentity(
    target: { dashboardId: string; dashboardVariant?: string } | null,
    dashboardId: string,
    dashboardVariant?: string,
  ): boolean {
    if (!target) {
      return false;
    }
    if (target.dashboardId !== dashboardId) {
      return false;
    }
    return (target.dashboardVariant ?? '') === (dashboardVariant ?? '');
  }
}
